import React, { useEffect, useState } from "react";
import axios from "axios";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { Button, Input, Selector, ToggleSwitch } from "globalComponents";
import { addNotification } from "store/slices/uiSlice";
import { setUser } from "store/slices/userSlice";
import { notifTags } from "globalComponents/Notification";
import { errorHandler } from "globalResources/util";
import { days } from "globalResources/constants";
import { getTheme } from "Theme";

const theme = getTheme();

// Payroll weeks use these. US zones under their canonical names; a company
// saved with another name (e.g. "US/Central") gets it added to the list.
const TIMEZONES = [
  { label: "Eastern", value: "America/New_York" },
  { label: "Central", value: "America/Chicago" },
  { label: "Mountain", value: "America/Denver" },
  { label: "Mountain (Arizona, no DST)", value: "America/Phoenix" },
  { label: "Pacific", value: "America/Los_Angeles" },
  { label: "Alaska", value: "America/Anchorage" },
  { label: "Hawaii", value: "Pacific/Honolulu" },
];
// older names the same zones are saved under (the dev seed uses US/Pacific)
const OLD_NAMES = {
  "US/Eastern": "Eastern",
  "US/Central": "Central",
  "US/Mountain": "Mountain",
  "US/Arizona": "Mountain (Arizona, no DST)",
  "US/Pacific": "Pacific",
  "US/Alaska": "Alaska",
  "US/Hawaii": "Hawaii",
};
const DAYS = days.filter((d) => d.value !== "none");

const Form = styled.fieldset`
  border: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 640px;
  margin: 0 auto;
  background: ${theme.PAGE_BACKGROUND};
  padding: 20px;
`;
const Row = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  > * {
    flex: 1;
    min-width: 220px;
  }
`;
const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-weight: 700;
`;
const TextArea = styled.textarea`
  padding: 8px;
  border-radius: 5px;
  border: 2px solid ${theme.INPUT_BORDER};
  min-height: 70px;
  font: inherit;
  font-weight: 400;
  resize: vertical;
`;
const Note = styled.p`
  margin: 0;
  opacity: 0.75;
  font-size: 0.95em;
`;
const ReadOnly = styled.p`
  margin: 0 0 6px 0;
  font-weight: 600;
  color: ${theme.BUTTON_COLOR_GREY};
`;
const Error = styled.p`
  margin: 0;
  color: ${theme.NEGATIVE};
  font-weight: 600;
`;

const fields = (company) => ({
  name: company.name || "",
  description: company.description || "",
  workweekStartDay: company.workweekStartDay || "monday",
  accountingTimezone: company.accountingTimezone || "US/Central",
  openToJoinRequests: !!company.openToJoinRequests,
});

// Company page "Settings" tab. Everyone who can open the page sees these;
// only manageCompany (or super) can save.
const Settings = () => {
  const dispatch = useDispatch();
  const account = useSelector((state) => state.current.account);
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get("/company")
      .then((res) => {
        setSaved(fields(res.data.company));
        setForm(fields(res.data.company));
        setCanEdit(res.data.canEdit);
      })
      .catch(async (err) => {
        const { message } = await errorHandler(err);
        setError(message || "Couldn't load the company settings");
      });
  }, []);

  if (!form) return error ? <Error>{error}</Error> : <p>Loading...</p>;

  const update = (name, value) => {
    setForm((old) => ({ ...old, [name]: value }));
    setError("");
  };
  const changed = Object.keys(form).filter((k) => form[k] !== saved[k]);
  const timezones = TIMEZONES.some((t) => t.value === form.accountingTimezone)
    ? TIMEZONES
    : [
        ...TIMEZONES,
        {
          label: OLD_NAMES[form.accountingTimezone]
            ? `${OLD_NAMES[form.accountingTimezone]} (${form.accountingTimezone})`
            : form.accountingTimezone,
          value: form.accountingTimezone,
        },
      ];

  const save = async () => {
    if (!changed.length || saving) return;
    setSaving(true);
    setError("");
    try {
      const body = Object.fromEntries(changed.map((k) => [k, form[k]]));
      const res = await axios.patch("/company", body);
      const next = fields(res.data.company);
      setSaved(next);
      setForm(next);
      // the company name in the header
      if (body.name)
        dispatch(setUser({ account: { ...account, name: next.name } }));
      dispatch(
        addNotification({
          type: notifTags.success,
          message: "Company settings saved",
        }),
      );
    } catch (err) {
      const { message } = await errorHandler(err);
      // errorHandler makes every 400 "Provided values are invalid"
      const apiMessage = err.response?.data;
      setError(
        (typeof apiMessage === "string" && apiMessage) ||
          message ||
          "Couldn't save",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form disabled={!canEdit} data-testid="company-settings">
      {!canEdit && (
        <ReadOnly>
          You can see these settings. Changing them needs the manageCompany
          permission.
        </ReadOnly>
      )}
      <Row>
        <Input
          label="Company Name"
          name="companyName"
          value={form.name}
          onChange={(e) => update("name", e.currentTarget.value)}
        />
      </Row>
      <Label>
        Description
        <TextArea
          name="companyDescription"
          maxLength={500}
          value={form.description}
          onChange={(e) => update("description", e.currentTarget.value)}
        />
      </Label>
      <Row>
        <Selector
          label="Work Week Starts On"
          options={DAYS}
          value={form.workweekStartDay}
          onSelect={(v) => update("workweekStartDay", v)}
        />
        <Selector
          label="Accounting Timezone"
          options={timezones}
          value={form.accountingTimezone}
          onSelect={(v) => update("accountingTimezone", v)}
        />
      </Row>
      <Note>
        Payroll weeks use the week start and timezone for everyone who doesn't
        have their own set on their profile. Changing them affects upcoming
        payroll, not payroll that already ran.
      </Note>
      <ToggleSwitch
        name="openToJoinRequests"
        label="Let anyone find this company and ask to join"
        value={form.openToJoinRequests}
        onChange={(on) => update("openToJoinRequests", on)}
      />
      <Note>
        When this is on, people signing up can pick this company from a list
        instead of entering your code. You still accept each request.
      </Note>
      {error && <Error>{error}</Error>}
      {canEdit && (
        <div>
          <Button onClick={save} disabled={!changed.length || saving}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      )}
    </Form>
  );
};

export default Settings;
